import { useCallback, useEffect, useState } from 'react';
import { PALETTES, TOKENS } from './theme';

// ── light/dark for the Shona TTS pages ───────────────────────────────────────
// The palette lives in CSS variables on :root, not in React state, so a toggle
// costs one style write instead of a re-render of every page. `colors.accent`
// in any component is the string `var(--sn-accent)`; changing what that
// variable resolves to repaints everything at once, including nodes React does
// not own (the play bar's inline gradients, injected <style> blocks).
//
// State here therefore tracks only the *name* of the mode, for the toggle's
// own label and aria-pressed. The colours are applied imperatively.

// Also read by the boot script in public/index.html, which paints the loading
// screen before this module exists — rename it in both places.
const STORAGE_KEY = 'dzose-theme-mode';

// Light unless the visitor has picked a mode with the toggle. The OS
// prefers-color-scheme setting is deliberately ignored: light is the designed
// default, and dark is something you opt into.
const DEFAULT_MODE = 'light';

// An unrecognised stored value is ignored rather than trusted, so a stale key
// can't paint an undefined palette.
const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Safari in private mode throws on localStorage access; fall back to the
    // default rather than failing to render.
  }
  return null;
};

export const initialMode = () => {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  return readStoredMode() ?? DEFAULT_MODE;
};

// Many controls carry `transition: background 0.15s, color 0.15s` on values
// that are var(--sn-*). When the variable changes underneath a transitioned
// property, the element animates from its old colour — and in practice it can
// simply keep the old one: the voice pills and the toggle itself stayed on the
// previous palette after a switch while untransitioned elements updated.
// Killing transitions for the duration of the swap fixes that, and stops the
// whole page cross-fading between two palettes, which looked like a bug too.
const SUPPRESS_CLASS = 'sn-theme-switching';
const SUPPRESS_CSS = `.${SUPPRESS_CLASS}, .${SUPPRESS_CLASS} *, .${SUPPRESS_CLASS} *::before, .${SUPPRESS_CLASS} *::after { transition: none !important; }`;

const ensureSuppressStyle = () => {
  if (document.getElementById('sn-theme-switching-style')) return;
  const el = document.createElement('style');
  el.id = 'sn-theme-switching-style';
  el.textContent = SUPPRESS_CSS;
  document.head.appendChild(el);
};

// Writes the palette onto :root. Exported so it can run before React mounts,
// which is what stops the first paint flashing the wrong palette.
export const applyMode = mode => {
  if (typeof document === 'undefined') return;
  const palette = PALETTES[mode] ?? PALETTES.light;
  const root = document.documentElement;

  ensureSuppressStyle();
  root.classList.add(SUPPRESS_CLASS);

  TOKENS.forEach(token => {
    root.style.setProperty(`--sn-${token}`, palette[token]);
  });
  // Tells the UA to render form controls, scrollbars and the canvas behind the
  // page in the matching scheme — without it, a light page keeps dark
  // scrollbars and native selects.
  root.style.colorScheme = mode;
  root.dataset.snMode = mode;
  // src/index.css paints `html` from --color-background, which is switched by
  // a prefers-color-scheme media query — i.e. by the OS, which our toggle is
  // allowed to disagree with. Choosing light on a dark machine would otherwise
  // leave a near-black canvas showing through overscroll and any gap the page
  // does not cover. An inline style on the root outranks that rule without
  // touching --color-background, so the rest of the app keeps its own theming.
  root.style.backgroundColor = palette.bg;

  // Two frames: one for the new variables to be applied, one for that paint to
  // land. Removing the class in a single rAF can still catch the same frame as
  // the variable write, which re-arms the transitions we are suppressing.
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => root.classList.remove(SUPPRESS_CLASS));
    });
  } else {
    root.classList.remove(SUPPRESS_CLASS);
  }
};

// Paint the palette at import time, before any component renders. Without this
// the first frame draws against unset variables — every colour falling back to
// the browser default — and the page visibly snaps once the first effect runs.
applyMode(initialMode());

const useThemeMode = () => {
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    applyMode(mode);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Choice still applies for this session; it just won't persist.
      }
      return next;
    });
  }, []);

  return [mode, toggle];
};

export default useThemeMode;
